import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  ValueStyled,
  TitleStyled,
  BoxStyled,
  TypographyStyled,
  InputBoxStyled,
  InfoButtonStyled
} from './style';
import { ImageTooltip } from './ImageTooltip';
import { Box, Divider, Modal } from '@mui/material';
import { UnknownAction } from '@reduxjs/toolkit';
import infoIcon from '../../../../assets/icons/infoIcon.svg';
import { PlaceholderGrid, CutOffIcon } from '../../../../styles/globalStyles';
import { Dropdown } from '../../../../shared/components/dropdown';
import { diseaseState } from '../../../../shared/shared.constants';
import { BMICard } from '../../../../shared/components/bmiCard';
import { StressFactorType } from '../../../../shared/shared.interfaces';
import treatmentIcon from '../../../../assets/icons/treatmentIcon.svg';
import dataMonitoringIcon from '../../../../assets/icons/dataMonitoringIcon.svg';
import { SingleNumberCard } from '../../../../shared/components/numberInput/numberCards/singleNumberCard';

export const StressFactor = ({
  stressFactor,
  BMRVal,
  setStressFactor
}: {
  stressFactor: StressFactorType;
  setStressFactor: (stress: StressFactorType) => UnknownAction;
  BMRVal: number;
}) => {
  const dispatch = useDispatch();
  const val = stressFactor.SFRange.split(' - ');
  const max = val[1] ? val[1] : val[0];
  const [inputRange, setInputRange] = useState({
    min: Number(val[0]),
    max: Number(max)
  });
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const val = stressFactor.SFRange.split(' - ');
    const max = val[1] ? val[1] : val[0];
    setInputRange({ min: Number(val[0]), max: Number(max) });
  }, [stressFactor.SFRange]);

  const getAdjustedValue = (value: number) => {
    return Number(
      diseaseState
        .filter((data) => data.value === value)[0]
        .range.split(' - ')[0]
    );
  };

  useEffect(() => {
    const bmrSFValue = Number((BMRVal + stressFactor.SFValue).toFixed(2));
    dispatch(
      setStressFactor({
        ...stressFactor,
        bmrPlusStressFactor: bmrSFValue
      })
    );
  }, [BMRVal, stressFactor.SFValue]);

  return (
    <>
      <CutOffIcon>
        <img src={dataMonitoringIcon} alt="monitoringIcon" />
      </CutOffIcon>
      <PlaceholderGrid
        container
        width={'100%'}
        sx={{ marginBottom: '1.25rem' }}
      >
        <InfoButtonStyled onClick={() => handleOpen()}>
          <img src={infoIcon} alt={'infoIcon'} />
        </InfoButtonStyled>
        <TypographyStyled variant="h3">STRESS FACTOR</TypographyStyled>
        <Dropdown
          label="Select disease state"
          data={diseaseState}
          value={stressFactor.optionSelected}
          handleChange={(e) => {
            dispatch(
              setStressFactor({
                optionSelected: e.target.value,
                SFRange: diseaseState.filter(
                  (val) => val.value === e.target.value
                )[0].range,
                adjustedValue: getAdjustedValue(e.target.value),
                SFValue: Number(
                  (BMRVal * (getAdjustedValue(e.target.value) / 100)).toFixed(2)
                ),
                bmrPlusStressFactor: Number(
                  (
                    BMRVal +
                    Number(
                      (
                        BMRVal *
                        (getAdjustedValue(e.target.value) / 100)
                      ).toFixed(2)
                    )
                  ).toFixed(2)
                )
              })
            );
          }}
        />
        <BoxStyled>
          <TitleStyled variant="h2">{'Stress Factor Range(%):'}</TitleStyled>
          <ValueStyled variant="h2">{stressFactor.SFRange || '+0'}</ValueStyled>
        </BoxStyled>
        <Divider sx={{ width: '100%' }} />
        <InputBoxStyled>
          <SingleNumberCard
            width={'100%'}
            title={'STRESS FACTOR ADJUSTMENT'}
            subHead={'(%)'}
            range={{ min: inputRange.min, max: inputRange.max }}
            value={stressFactor.adjustedValue}
            setValue={(val: number) => {
              dispatch(
                setStressFactor({
                  ...stressFactor,
                  adjustedValue: Number(val),
                  SFValue: Number((BMRVal * (val / 100)).toFixed(2))
                })
              );
            }}
          />
        </InputBoxStyled>
        <BoxStyled>
          <TitleStyled variant="h2">{'Stress Factor (kcal):'}</TitleStyled>
          <ValueStyled variant="h2">
            {stressFactor.SFValue >= 0
              ? `+${stressFactor.SFValue}`
              : stressFactor.SFValue}
          </ValueStyled>
        </BoxStyled>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ width: '100%', marginTop: '2.5rem' }}>
          <BMICard
            label1="BMR + STRESS FACTOR"
            label2={`${stressFactor.bmrPlusStressFactor} kcal`}
            icon={treatmentIcon}
          />
        </Box>
      </PlaceholderGrid>
      <Modal
        sx={{ overflow: 'scroll' }}
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ImageTooltip onClose={handleClose} />
      </Modal>
    </>
  );
};
